import React from 'react'
import { useSelector, useDispatch } from 'react-redux'
import {
  Box, Paper, Typography, Stepper, Step, StepLabel, LinearProgress,
  Alert, Button, Chip,
} from '@mui/material'
import { RotateCcw, FileText } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { startCleaning } from '../../../../redux/actions/boConsolidationAction'

const CLEANING_STEPS = [
  { key: 'download', label: 'Download' },
  { key: 'preprocess', label: 'Preprocess' },
  { key: 'index', label: 'Index' },
  { key: 'convert', label: 'Convert' },
  { key: 'save', label: 'Save' },
  { key: 'review', label: 'Review' },
]

export default function BOCleaningProgress() {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const { selectedBo, cleaningProgress } = useSelector((s) => s.boConsolidation)
  const { step, percent, message, docsFound } = cleaningProgress

  const activeStepIndex = CLEANING_STEPS.findIndex((s) => s.key === step)
  const isError = step === 'error'

  const handleRetry = () => {
    if (selectedBo) {
      dispatch(startCleaning(selectedBo.id))
    }
  }

  return (
    <Paper sx={{ p: 4, borderRadius: 2 }}>
      {/* Error Alert */}
      {isError && (
        <Alert
          severity="error"
          sx={{ mb: 3 }}
          action={
            <Button
              size="small"
              startIcon={<RotateCcw size={14} />}
              onClick={handleRetry}
              sx={{ textTransform: 'none' }}
            >
              {t('du_bo_retry', 'Retry')}
            </Button>
          }
        >
          {message || t('du_bo_cleaning_error', 'An error occurred during cleaning.')}
        </Alert>
      )}

      {/* Stepper */}
      {!isError && (
        <>
          <Stepper activeStep={activeStepIndex >= 0 ? activeStepIndex : 0} alternativeLabel sx={{ mb: 3 }}>
            {CLEANING_STEPS.map((s) => (
              <Step key={s.key}>
                <StepLabel
                  sx={{
                    '& .MuiStepIcon-root.Mui-active': { color: '#c9a96e' },
                    '& .MuiStepIcon-root.Mui-completed': { color: '#c9a96e' },
                  }}
                >
                  {t(`du_bo_step_${s.key}`, s.label)}
                </StepLabel>
              </Step>
            ))}
          </Stepper>

          {/* Progress Bar */}
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="body2" color="text.secondary">
                {message || t('du_bo_processing', 'Processing...')}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                {Math.round(percent)}%
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={percent}
              sx={{
                height: 6,
                borderRadius: 3,
                backgroundColor: 'rgba(201,169,110,0.15)',
                '& .MuiLinearProgress-bar': { backgroundColor: '#c9a96e', borderRadius: 3 },
              }}
            />
          </Box>

          {/* Docs found counter */}
          {docsFound > 0 && (
            <Chip
              icon={<FileText size={14} />}
              label={t('du_bo_docs_found', { n: docsFound })}
              size="small"
              variant="outlined"
              sx={{ mt: 1 }}
            />
          )}
        </>
      )}
    </Paper>
  )
}
